import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@lombok/api-client';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { ALL_PAGES_SIZE, fetchAllPages } from '@/data/pagination';
import { api } from '@/lib/api';
import { orderLabel } from '@/lib/names';
import { pricedItems } from '@/lib/orderView';
import { queryKeys } from '@/lib/queryClient';
import { findOrderable, planReorder } from './reorder';
import { useOrderCart } from './useOrderCart';

export const CREATE_ORDER_PATH = '/app/customer/crear-pedido';

/** What the create screen shows after a repeat (navigation state). */
export interface RepeatOrderNotice {
  repeatedFrom: string;
  skipped: string[];
}

/**
 * Repeat a past order: loads its lines that can still be ordered into the customer's order in
 * progress and opens the create screen, where current prices and availability are shown and the
 * customer reviews everything before sending.
 */
export function useRepeatOrder() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { profile } = useSessionProfile();
  const cart = useOrderCart(profile?.customerId);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const repeat = async (orderId: string) => {
    setError(null);
    setPendingId(orderId);
    try {
      const { order, items: lines } = await api.getOrder(orderId);
      const items = pricedItems(lines);
      const catalog = await qc.fetchQuery({
        queryKey: queryKeys.products({ catalog: 'all' }),
        queryFn: () => fetchAllPages((cursor) => api.listProducts({ limit: ALL_PAGES_SIZE, cursor })),
        staleTime: 60_000,
      });
      const orderable = await findOrderable(items, catalog.items, (productId) =>
        qc.fetchQuery({
          queryKey: queryKeys.skus(productId),
          queryFn: () => api.listSkus(productId),
          staleTime: 60_000,
        }),
      );
      const plan = planReorder(items, orderable);
      if (plan.items.length === 0) {
        setError('Ninguno de los productos de este pedido está disponible ahora.');
        return;
      }
      cart.replace(plan.items);
      const notice: RepeatOrderNotice = { repeatedFrom: orderLabel(order), skipped: plan.skipped };
      navigate(CREATE_ORDER_PATH, { state: notice });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo cargar el pedido. Intenta de nuevo.');
    } finally {
      setPendingId(null);
    }
  };

  return { repeat, pendingId, error };
}
